import axios from "axios";

const api = axios.create({
  // baseURL: "http://localhost:8009/murasalone/v2/api",
  // baseURL: "https://defiable-kristan-eductive.ngrok-free.dev/murasalone/v2/api",
  baseURL: "https://spacehubbackend.insyncproducts.online/murasalone/v2/api",
  // baseURL: "http://109.205.180.193:8011/murasalone/v2/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// ==============================
// REQUEST INTERCEPTOR
// ==============================
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ==============================
// RESPONSE INTERCEPTOR (Auto Refresh Token)
// ==============================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// error handling interceptor

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ✅ Skip refresh for auth endpoints to prevent infinite loops
    if (
      originalRequest.url === "/auth/refresh" ||
      originalRequest.url === "/auth/login"
    ) {
      return Promise.reject(error);
    }

    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
          throw new Error("No refresh token");
        }

        // Call refresh token endpoint
        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Save new tokens
        localStorage.setItem("accessToken", accessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process queued requests with new token
        processQueue(null, accessToken);

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Only force logout if there was actually an authenticated session.
        // Guest/anonymous sessions (load-test browsers) have no accessToken,
        // so a stray 401 here should just fail quietly instead of redirecting.
        if (localStorage.getItem("accessToken")) {
          localStorage.clear();
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip if already handled by refresh logic
    if (error.config?._retry) {
      return Promise.reject(error);
    }

    // ── Log all errors with context ──────────────────────────────────────
    const url = error.config?.url || "unknown";
    const method = error.config?.method?.toUpperCase() || "GET";
    const status = error.response?.status || "NETWORK";

    console.error(
      `[API Error] ${method} ${url} → ${status}`,
      status >= 500
        ? "Server error"
        : status >= 400
          ? "Client error"
          : "Network error",
      error.response?.data || error.message,
    );

    // ── Standardize error shape ──────────────────────────────────────────
    const standardizedError = {
      status,
      url,
      method,
      message:
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Unknown error",
      data: error.response?.data || null,
      timestamp: new Date().toISOString(),
      originalError: error,
    };

    // ── Specific handling by status code ─────────────────────────────────
    if (status === 401) {
      // Already handled by token refresh interceptor
    } else if (status === 403) {
      console.warn("[API] Access forbidden — user may lack permissions");
    } else if (status === 404) {
      console.warn(`[API] Resource not found: ${url}`);
    } else if (status === 422) {
      console.warn("[API] Validation error:", error.response?.data);
    } else if (status >= 500) {
      console.error("[API] Server error — the backend may be down");
    } else if (status === "NETWORK") {
      console.error("[API] Network error — check connectivity");
    }

    // ── Send to monitoring in production ─────────────────────────────────
    if (process.env.NODE_ENV === "production" && status >= 500) {
      // Sentry.captureException(standardizedError);
    }

    // Attach standardized error to the rejection
    error.standardized = standardizedError;
    return Promise.reject(error);
  },
);

// ======================================================
// AUTH APIs
// ======================================================

export const loginApi = (data) => api.post("/auth/login", data);
export const refreshTokenApi = (data) => api.post("/auth/refresh", data);
export const logoutApi = (data) => api.post("/auth/logout", data);
export const setPasswordApi = (data) => api.post("/auth/set-password", data);

// ======================================================
// USERS APIs
// ======================================================

export const getUsersApi = (page = 1, limit = 25, search = "") =>
  api.get(`/users?page=${page}&limit=${limit}&search=${search}`);

export const getUserByIdApi = (id) => api.get(`/users/${id}`);
export const createUserApi = (data) => api.post("/users", data);
export const updateUserApi = (id, data) => api.patch(`/users/${id}`, data);
export const updateUserStatusApi = (id, data) =>
  api.patch(`/users/${id}/status`, data);
export const resetUserPasswordApi = (id) =>
  api.post(`/users/${id}/reset-password`);
export const resetUserSessionsApi = (id) =>
  api.post(`/users/${id}/reset-sessions`);

// ======================================================
// ROLES APIs
// ======================================================

export const getRolesApi = () => api.get("/roles");
export const createRoleApi = (data) => api.post("/roles", data);
export const updateRoleApi = (id, data) => api.patch(`/roles/${id}`, data);
export const deleteRoleApi = (id) => api.delete(`/roles/${id}`);

// ======================================================
// DEPARTMENTS APIs
// ======================================================

export const getDepartmentsApi = () => api.get("/departments");
export const createDepartmentApi = (data) => api.post("/departments", data);

// ======================================================
// DASHBOARD APIs
// ======================================================

export const getDashboardSummaryApi = () => api.get("/dashboard/summary");

// ======================================================
// MINIO DIRECT UPLOAD APIs
// ======================================================

export const registerMediaApi = (data) => api.post("/media/register", data);
export const directUploadMediaApi = (formData) =>
  api.post("/media/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000, // 2 min timeout for large files
  });
export const updateProfileImageApi = (data) =>
  api.patch("/profile/image", data);
export const getPresignedUploadUrlApi = (data) =>
  api.post("/media/presigned-upload", data);

// ======================================================
// PROFILE APIs
// ======================================================

export const getProfileApi = () => api.get("/profile");
export const updateProfileApi = (data) => api.patch("/profile", data);
export const updatePasswordApi = (data) => api.patch("/profile/password", data);

// ======================================================
// PROFILE SESSION APIs
// ======================================================

export const getProfileSessionsApi = () => api.get("/profile/sessions");
export const revokeProfileSessionApi = (sessionId) =>
  api.delete(`/profile/sessions/${sessionId}`);
export const revokeAllProfileSessionsApi = () =>
  api.delete("/profile/sessions");

// ─────────────────────────────────────────────
// REPORT APIs
// ─────────────────────────────────────────────

export const getUserActivityReportApi = (
  page = 1,
  limit = 20,
  department = "",
  role = "",
  status = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/user-activity?page=${page}&limit=${limit}&department=${department}&role=${role}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getRoomActivityReportApi = (
  page = 1,
  limit = 20,
  department = "",
  roomType = "",
  status = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/room-activity?page=${page}&limit=${limit}&department=${department}&roomType=${roomType}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getWhatsappRoutingReportApi = (
  page = 1,
  limit = 20,
  status = "",
  type = "",
  phone = "",
  room = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/whatsapp-routing?page=${page}&limit=${limit}&status=${status}&type=${type}&phone=${phone}&room=${room}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getMediaStorageReportApi = (
  page = 1,
  limit = 20,
  fileType = "",
  status = "",
  room = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/media-storage?page=${page}&limit=${limit}&fileType=${fileType}&status=${status}&room=${room}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getSecurityEventsReportApi = (
  page = 1,
  limit = 20,
  action = "",
  user = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/security-events?page=${page}&limit=${limit}&action=${action}&user=${user}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getBroadcastEngagementReportApi = (
  page = 1,
  limit = 20,
  audienceType = "",
  status = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/broadcast-engagement?page=${page}&limit=${limit}&audienceType=${audienceType}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`,
  );

// ======================================================
// TEAMS APIs
// ======================================================

export const getTeamsApi = (
  page = 1,
  limit = 20,
  search = "",
  department = "",
  type = "",
  status = "",
) =>
  api.get(
    `/teams?page=${page}&limit=${limit}&search=${search}&department=${department}&type=${type}&status=${status}`,
  );

export const getTeamByIdApi = (id) => api.get(`/teams/${id}`);
export const createTeamApi = (data) => api.post("/teams", data);
export const updateTeamApi = (id, data) => api.patch(`/teams/${id}`, data);
export const deleteTeamApi = (id) => api.delete(`/teams/${id}`);
export const addTeamMemberApi = (teamId, data) =>
  api.post(`/teams/${teamId}/members`, data);
export const updateTeamMemberRoleApi = (teamId, userId, data) =>
  api.patch(`/teams/${teamId}/members/${userId}`, data);
export const removeTeamMemberApi = (teamId, userId) =>
  api.delete(`/teams/${teamId}/members/${userId}`);
export const createTeamChannelApi = (teamId, data) =>
  api.post(`/teams/${teamId}/channels`, data);
export const updateChannelApi = (channelId, data) =>
  api.patch(`/teams/channels/${channelId}`, data);
export const getTeamAuditLogsApi = (teamId, page = 1, limit = 20) =>
  api.get(`/teams/${teamId}/audit?page=${page}&limit=${limit}`);

// ======================================================
// CHANNEL MEMBERS APIs
// ======================================================

export const getChannelMembersApi = (channelId) =>
  api.get(`/teams/channels/${channelId}/members`);
export const addChannelMemberApi = (channelId, data) =>
  api.post(`/teams/channels/${channelId}/members`, data);
export const removeChannelMemberApi = (channelId, userId) =>
  api.delete(`/teams/channels/${channelId}/members/${userId}`);

// ======================================================
// AUDIT APIs
// ======================================================

export const getAuditLogsApi = (page = 1, limit = 20) =>
  api.get(`/audit?page=${page}&limit=${limit}`);
export const getAuditLogByIdApi = (id) => api.get(`/audit/${id}`);

// ======================================================
// BROADCAST APIs
// ======================================================

export const getBroadcastsApi = (
  page = 1,
  limit = 10,
  search = "",
  status = "",
  type = "",
  priority = "",
) =>
  api.get(
    `/broadcasts?page=${page}&limit=${limit}&search=${search}&status=${status}&type=${type}&priority=${priority}`,
  );

export const getBroadcastByIdApi = (id) => api.get(`/broadcasts/${id}`);
export const createBroadcastApi = (data) => api.post("/broadcasts", data);
export const updateBroadcastApi = (id, data) =>
  api.patch(`/broadcasts/${id}`, data);
export const updateBroadcastStatusApi = (id, data) =>
  api.patch(`/broadcasts/${id}/status`, data);
export const deleteBroadcastApi = (id) => api.delete(`/broadcasts/${id}`);
export const resendBroadcastApi = (id) => api.post(`/broadcasts/${id}/resend`);
export const getBroadcastTemplatesApi = () => api.get("/broadcasts/templates");

// ─────────────────────────────────────────────
// WHATSAPP MAPPING APIs
// ─────────────────────────────────────────────

export const getWhatsappMappingsApi = () => api.get("/whatsapp/mappings");
export const createWhatsappMappingApi = (data) =>
  api.post("/whatsapp/mappings", data);
export const updateWhatsappMappingApi = (id, data) =>
  api.patch(`/whatsapp/mappings/${id}`, data);
export const deleteWhatsappMappingApi = (id) =>
  api.delete(`/whatsapp/mappings/${id}`);

// ─────────────────────────────────────────────
// WHATSAPP GROUP APIs
// ─────────────────────────────────────────────

export const getWhatsappGroupsApi = () => api.get("/whatsapp/groups");

// ───────────────── MEDIA REPOSITORY ─────────────────

export const getMediaRepositoryApi = (
  page = 1,
  limit = 20,
  search = "",
  status = "",
  type = "",
  room = "",
) =>
  api.get(
    `/media?page=${page}&limit=${limit}&search=${search}&status=${status}&type=${type}&room=${room}`,
  );

export const getSingleMediaApi = (id) => api.get(`/media/${id}`);
export const quarantineMediaApi = (id) => api.patch(`/media/${id}/quarantine`);
export const restoreMediaApi = (id) => api.patch(`/media/${id}/restore`);
export const deleteMediaApi = (id) => api.delete(`/media/${id}`);

// ─────────────────────────────────────────────
// WHATSAPP USERS APIs
// ─────────────────────────────────────────────

export const getWhatsappUsersApi = () => api.get("/whatsapp/users");

// ======================================================
// SEARCH APIs
// ======================================================

export const searchUsersApi = (query) => api.get(`/users/search?q=${query}`);

// ======================================================
// DEVICE APIs
// ======================================================

export const registerDeviceTokenApi = (data) =>
  api.post("/devices/token", data);

// ======================================================
// CALL APIs
// ======================================================

export const startCallApi = (data) => api.post("/calls/start", data);
export const acceptCallApi = (callId) => api.post(`/calls/${callId}/accept`);
export const endCallApi = (callId) => api.post(`/calls/${callId}/end`);
export const joinCallApi = (callId) => api.post(`/calls/${callId}/join`);
export const startGroupCallApi = (groupId, data) =>
  api.post(`/calls/groups/${groupId}/start`, data);

// ─────────────────────────────────────────────
// SUPPORT TICKETS APIs
// ─────────────────────────────────────────────

export const getSupportTicketsApi = (
  page = 1,
  limit = 20,
  status = "",
  priority = "",
  search = "",
) =>
  api.get(
    `/support/tickets?page=${page}&limit=${limit}&status=${status}&priority=${priority}&search=${search}`,
  );

export const getSupportTicketByIdApi = (id) =>
  api.get(`/support/tickets/${id}`);
export const createSupportTicketApi = (data) =>
  api.post("/support/tickets", data);
export const resolveSupportTicketApi = (id) =>
  api.patch(`/support/tickets/${id}/resolve`);
export const addSupportTicketNoteApi = (id, data) =>
  api.post(`/support/tickets/${id}/notes`, data);

// ─────────────────────────────────────────────
// SUPPORT FAQ APIs
// ─────────────────────────────────────────────

export const getSupportFaqsApi = () => api.get("/support/faqs");
export const createSupportFaqApi = (data) => api.post("/support/faqs", data);
export const updateSupportFaqApi = (id, data) =>
  api.patch(`/support/faqs/${id}`, data);
export const deleteSupportFaqApi = (id) => api.delete(`/support/faqs/${id}`);

// ─────────────────────────────────────────────
// SUPPORT ESCALATION APIs
// ─────────────────────────────────────────────

export const getSupportEscalationsApi = () => api.get("/support/escalations");

// ======================================================
// HEALTH APIs
// ======================================================

export const getHealthApi = () => api.get("/health");

// ─────────────────────────────────────────────
// WHATSAPP ROUTING LOG APIs
// ─────────────────────────────────────────────

export const getWhatsappLogsApi = (
  page = 1,
  limit = 20,
  status = "",
  type = "",
  search = "",
) =>
  api.get(
    `/whatsapp/logs?page=${page}&limit=${limit}&status=${status}&type=${type}&search=${search}`,
  );

export const getWhatsappLogByIdApi = (id) => api.get(`/whatsapp/logs/${id}`);
export const retryWhatsappLogApi = (id) =>
  api.patch(`/whatsapp/logs/${id}/retry`);

// ======================================================
// SECURITY DASHBOARD APIs
// ======================================================

export const getSecurityDashboardApi = (range = "7d") =>
  api.get(`/security/dashboard?range=${range}`);
export const getSecurityAlertsApi = () => api.get("/security/alerts");
export const getSecurityRecentActivityApi = (limit = 20) =>
  api.get(`/security/recent-activity?limit=${limit}`);
export const sendSecurityAlertApi = (data) =>
  api.post("/security/send-alert", data);

export const getQuarantineReportApi = (page = 1, limit = 20) =>
  api.get(`/security/quarantine-report?page=${page}&limit=${limit}`);

export const getScanReportApi = (range = "7d") =>
  api.get(`/security/scan-report?range=${range}`);

// ======================================================
// MEETINGS APIs
// ======================================================

export const getMeetingsApi = (params = {}) =>
  api.get("/calendars/events", { params });

export const getMeetingJoinTokenApi = (meetingId) =>
  api.get(`/meetings/join-token/${meetingId}`);

export const createMursalMeetingApi = (data) =>
  api.post("/meetings/livekit", data);

export const createExchangeMeetingApi = (data) =>
  api.post("/meetings/exchange2019", data);

export const getAllMeetingsApi = (params = {}) =>
  api.get("/meetings", { params });

export const getMeetingByIdApi = (meetingId) =>
  api.get(`/meetings/${meetingId}`);

export const updateMeetingApi = (meetingId, data) =>
  api.patch(`/meetings/${meetingId}`, data);

export const cancelMeetingApi = (meetingId) =>
  api.delete(`/meetings/${meetingId}`);

export const addParticipantApi = (meetingId, data) =>
  api.post(`/meetings/${meetingId}/participants`, data);

export const removeParticipantApi = (meetingId, participantId) =>
  api.delete(`/meetings/${meetingId}/participants/${participantId}`);

export const getMeetingStatsApi = () => api.get("/meetings/stats");

// ======================================================
// MEETING RECORDING APIs
// ======================================================

export const startMeetingRecordingApi = (meetingId) =>
  api.post(`/meetings/recording/start/${meetingId}`);

export const stopRecordingApi = (recordingId) =>
  api.post(`/meetings/recordings/stop/${recordingId}`);

export const getMeetingRecordingsApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/recordings`);

// ======================================================
// MEETING TRANSCRIPT APIs
// ======================================================

export const processTranscriptApi = (meetingId) =>
  api.post(`/meetings/transcript/process/${meetingId}`);

// ======================================================
// SCREEN SHARE APIs
// ======================================================

export const canShareScreenApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/can-share-screen`);

export const startScreenShareApi = (meetingId, platform = "web") =>
  api.post(`/meetings/${meetingId}/screen-share/start`, { platform });

export const stopScreenShareApi = (meetingId, platform = "web") =>
  api.post(`/meetings/${meetingId}/screen-share/stop`, { platform });

export const hostStopScreenShareApi = (meetingId, targetUserId) =>
  api.post(`/meetings/${meetingId}/host/stop-screen-share`, { targetUserId });

export const getScreenShareStatusApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/screen-share-status`);

export const getScreenShareLogsApi = (meetingId, params = {}) =>
  api.get(`/meetings/${meetingId}/screen-share-logs`, { params });

// ======================================================
// CALENDAR SYNC APIs
// ======================================================

export const syncGraphCalendarApi = (data) =>
  api.post("/calendar-sync/graph", data);

// ======================================================
// LIVEKIT WEBHOOK (server-to-server)
// ======================================================

export const livekitWebhookApi = (data) => api.post("/webhooks/livekit", data);

// ======================================================
// MEETING PARTICIPANT CONTROL APIs
// ======================================================

export const kickParticipantApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/kick/${participantId}`);

export const muteParticipantApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/mute/${participantId}`);

export const unmuteParticipantApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/unmute/${participantId}`);

export const disableParticipantVideoApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/disable-video/${participantId}`);

export const enableParticipantVideoApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/enable-video/${participantId}`);

export const lockMeetingApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/lock`);

export const unlockMeetingApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/unlock`);

export const endMeetingForAllApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/end`);

// Get full transcripts for a meeting
export const getMeetingTranscriptsApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/transcripts`);

// Get full minutes for a meeting
export const getMeetingMinutesApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/minutes`);

export const joinMeetingApi = (meetingId) =>
  api.post(`/meetings/join/${meetingId}`);

export const leaveMeetingApi = (meetingId) =>
  api.post(`/meetings/leave/${meetingId}`);

export const requestToJoinWaitingRoomApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/waiting-room/request`);

export const checkWaitingRoomStatusApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/waiting-room/check`);

export const getPendingWaitingRoomRequestsApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/waiting-room/pending`);

export const approveWaitingRoomParticipantApi = (meetingId, userId) =>
  api.post(`/meetings/${meetingId}/waiting-room/approve/${userId}`);

export const rejectWaitingRoomParticipantApi = (meetingId, userId) =>
  api.post(`/meetings/${meetingId}/waiting-room/reject/${userId}`);

export const getAttendeeMeetingsApi = (params = {}) =>
  api.get("/meetings/attendee", { params });

export const reprocessTranscriptApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/reprocess`);

export const getMediaPreviewBlobApi = (mediaId) =>
  api.get(`/media/preview/${mediaId}`, { responseType: "blob" });

// ======================================================
// GUEST JOIN APIs (No Auth Required - Load Testing)
// ======================================================

export const guestJoinMeetingApi = (meetingId, name) =>
  api.post(`/guest/join/${meetingId}`, { name });

export const guestRejoinMeetingApi = (meetingId, guestId, name) =>
  api.post(`/guest/join/${meetingId}/approved`, { guestId, name });

export default api;
